import { useCallback, useEffect, useRef, useState } from 'react'
import AccountAccessForgetPassword from './AccountAccessForgetPassword'
import AccountAccessJoin from './AccountAccessJoin'
import AccountAccessLoginStart from './AccountAccessLoginStart'
import AccountAccessLoginPassword from './AccountAccessLoginPassword'
import AccountAccessResetPassword from './AccountAccessResetPassword'
import config from 'constants/config'
import AgentHubLoginForm from 'agentHub/components/AgentHubAccountAccess/AgentHubLoginForm'
import AgentHubLoginV2Form from 'agentHub/components/AgentHubAccountAccess/AgentHubLoginV2Form'
import useIsLoginV2Enabled from 'agentHub/hooks/useIsLoginV2Enabled'
import AgentHubPasswordLoginForm from 'agentHub/components/AgentHubAccountAccess/AgentHubPasswordLoginForm'
import AgentHubResendActivationEmail from 'agentHub/components/AgentHubAccountAccess/AgentHubResendActivationEmail'
import AccountAccessLoginOTP from './AccountAccessLoginOTP'
import AccountAccessAddPhone from './AccountAccessAddPhone'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import AccountAccessJoinExistingAccount from './AccountAccessJoinExistingAccount'
import { clearAuthError } from 'actions/AuthActions'
import AccountAccessAppleRandomEmail from './AccountAccessAppleRandomEmail'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { isLastUsedLoginMethod, setLastUsedLoginMethod } from 'lib/auth/lastUsedLoginMethod'
import AccountAccessSignUpOTP from './AccountAccessSignUpOTP'
import type { PendingSignUpOTPData } from './types'

interface Props {
  mode?: App.UiAccountModalMode
  initialMode?: App.UiAccountModalMode
  dismissable?: boolean
  defaultEmail?: string
  onModeChange?: (nextMode: App.UiAccountModalMode) => void
  resetPasswordToken?: string
  hotelOfferDetails?: App.AccountModalOfferDetails
  offerDetails?: App.AccountModalOfferDetails
  callbackPath?: string
  defaultAppleData?: App.AppleLoginData
  secondChanceDismissed?: boolean
  onSecondChanceDismissedChange?: (dismissed: boolean) => void
  hideLoginPasswordBackButton?: boolean
  /** Metasearch guest checkout: offers a "Continue without signing in" action on the login step. */
  guestCheckoutEnabled?: boolean
}

function AccountAccessModalContent(props: Props) {
  const {
    mode,
    initialMode,
    defaultEmail,
    onModeChange,
    resetPasswordToken,
    hotelOfferDetails,
    offerDetails,
    callbackPath,
    dismissable,
    defaultAppleData,
    secondChanceDismissed,
    onSecondChanceDismissedChange,
    hideLoginPasswordBackButton,
    guestCheckoutEnabled,
  } = props

  const [userId, setUserId] = useState<string>(defaultEmail ?? '')
  const [applePrivateEmailData, setApplePrivateEmailData] = useState<App.AppleLoginData | null>(defaultAppleData ?? null)
  const [pendingSignUpOTPData, setPendingSignUpOTPData] = useState<PendingSignUpOTPData | null>(null)
  const isAgentHubMode = config.agentHub.isEnabled
  const isLoginV2Enabled = useIsLoginV2Enabled()
  const user = useAppSelector(state => state.auth.users[userId])
  const loggedIn = useAppSelector(selectLoggedIn)
  const authSource = useAppSelector(state => state.auth.source)
  const dispatch = useAppDispatch()
  const wasLoggedInRef = useRef(loggedIn)

  const onChangeMode = useCallback((nextMode: App.UiAccountModalMode, nextUserId?: string) => {
    // errors are relevant only for the life of that 'mode', so reset them as we change
    dispatch(clearAuthError())
    onModeChange?.(nextMode)

    if (nextUserId !== undefined) {
      setUserId(nextUserId)
    }
  }, [dispatch, onModeChange])

  useEffect(() => {
    if (!wasLoggedInRef.current && loggedIn && authSource && isLastUsedLoginMethod(authSource)) {
      setLastUsedLoginMethod(authSource)
    }

    wasLoggedInRef.current = loggedIn
  }, [loggedIn, authSource])

  switch (mode) {
    case 'login':
    case 'requestAgentCallbackLogin':
    case 'tripPlannerLogin':
    case 'tripPlannerPagesLogin':
    case 'wishlistLogin':
    case 'toursCatalogueLogin':
    case 'loyaltyLogin':
    case 'loyaltyTierUpgradeCampaignLogin':
    case 'statusMatchLogin':
    case 'sharedCartInvite':
    case 'bookingAuthorityInvite':
    case 'supportLogin':
    case 'leLiveLogin':
      if (isAgentHubMode) {
        // When ?login-v2=true (leagenthub only), replace the v1 email-entry form
        // with the single-button Auth V2 (OIDC) sign-in.
        if (isLoginV2Enabled) {
          return <AgentHubLoginV2Form dismissable={dismissable} />
        }
        return <AgentHubLoginForm dismissable={dismissable} defaultUser={user} onSubmit={setUserId} onModeChange={onChangeMode} />
      } else {
        return <AccountAccessLoginStart
          mode={mode}
          onModeChange={onChangeMode}
          hotelOfferDetails={hotelOfferDetails}
          offerDetails={offerDetails}
          dismissable={dismissable}
          applePrivateEmailData={applePrivateEmailData}
          setApplePrivateEmailData={setApplePrivateEmailData}
          guestCheckoutEnabled={guestCheckoutEnabled}
        />
      }
    case 'loginPassword':
      if (user) {
        if (isAgentHubMode) {
          return <AgentHubPasswordLoginForm user={user} onModeChange={onChangeMode} />
        } else {
          return <AccountAccessLoginPassword
            user={user}
            onModeChange={onChangeMode}
            dismissable={dismissable}
            hideBackButton={hideLoginPasswordBackButton}
          />
        }
      }
      return null
    case 'loginPasswordOTP':
      if (user) {
        return <AccountAccessLoginPassword
          otpEnabled
          user={user}
          onModeChange={onChangeMode}
          dismissable={dismissable}
          hideBackButton={hideLoginPasswordBackButton}
        />
      }
      return null
    case 'loginOTP':
      if (user) {
        return <AccountAccessLoginOTP user={user} onModeChange={onChangeMode} dismissable={dismissable} />
      }
      return null
    case 'promptAddPhone':
    case 'promptAddPhoneOnboarding':
      return <AccountAccessAddPhone
        dismissable={false}
        trackPromptImpression={mode !== 'promptAddPhoneOnboarding'}
        hasDismissed={secondChanceDismissed}
        onDismissChange={onSecondChanceDismissedChange}
      />
    case 'join':
      if (user) {
        return <AccountAccessJoin
          user={user}
          onModeChange={onChangeMode}
          onPendingSignUpOTPData={setPendingSignUpOTPData}
          dismissable={dismissable}
          signupContext={initialMode}
        />
      }
      return null
    case 'joinOTP':
      if (pendingSignUpOTPData) {
        return <AccountAccessSignUpOTP
          pendingSignUpOTPData={pendingSignUpOTPData}
          onModeChange={onChangeMode}
          hasDismissed={secondChanceDismissed}
        />
      }
      return null
    case 'joinExistingAccount':
      if (user) {
        return <AccountAccessJoinExistingAccount user={user} onModeChange={onChangeMode} dismissable={dismissable} />
      }
      return null
    case 'forgotPassword':
      if (user) {
        return <AccountAccessForgetPassword user={user} onModeChange={onChangeMode} dismissable={dismissable} />
      }
      return null
    case 'resetPassword':
      return <AccountAccessResetPassword token={resetPasswordToken!} callbackPath={callbackPath} dismissable={dismissable} />
    case 'agentResendActivation':
      if (isAgentHubMode && user?.email) {
        return <AgentHubResendActivationEmail requestedEmail={user.email} onModeChange={onChangeMode} />
      }
      return null
    case 'applePrivateEmail':
      if (user) {
        return <AccountAccessAppleRandomEmail
          user={user}
          onModeChange={onChangeMode}
          applePrivateEmailData={applePrivateEmailData}
          setApplePrivateEmailData={setApplePrivateEmailData}
        />
      }
      return null
    default:
      return null
  }
}

export default AccountAccessModalContent
